import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, List, X, SignOut, PawPrint, CaretDown } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  { label: "Shop", items: [
    { to: "/shop", label: "All Merch" },
    { to: "/pre-order", label: "Pre-Order" },
    { to: "/signature", label: "The Signature Line" },
    { to: "/mission", label: "Mission Drop" },
    { to: "/sets", label: "The Sets" },
    { to: "/bandanas", label: "Bandanas" },
    { to: "/dog-gear", label: "Dog Gear" },
  ]},
  { label: "Mission", items: [
    { to: "/rescue", label: "Rescue" },
    { to: "/foster", label: "Foster Portal" },
    { to: "/save-the-children", label: "Save Kids" },
    { to: "/ranch", label: "The Ranch" },
    { to: "/donate", label: "Donate" },
    { to: "/sponsor", label: "Sponsor a Pit" },
  ]},
  { label: "Studio", items: [
    { to: "/collab", label: "1-of-1 Collab" },
    { to: "/try-on", label: "Virtual Try-On" },
    { to: "/build", label: "Build-A-Fit" },
    { to: "/artworkz", label: "ArtWorkz" },
  ]},
  { label: "Community", items: [
    { to: "/collabs", label: "City Collabs" },
    { to: "/wrap", label: "Neon Wrap" },
    { to: "/hub", label: "Mr Hub" },
    { to: "/contact", label: "Contact" },
  ]},
];

export default function Navbar() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const itemClass = ({ isActive }) =>
    `block px-4 py-2.5 text-sm rounded-lg transition-colors ${isActive ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-white hover:bg-white/5"}`;

  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <PawPrint weight="fill" className="text-primary" size={22} />
          <span className="font-modern text-lg font-semibold tracking-tight">
            MR<span style={{ color: "#ccff00", textShadow: "0 0 7px #ccff00" }}>.</span>WIGGLEZ
          </span>
        </Link>

        {/* Desktop grouped dropdowns */}
        <nav className="hidden lg:flex items-center gap-2">
          {GROUPS.map((g) => (
            <div key={g.label} className="relative group" data-testid={`nav-group-${g.label.toLowerCase()}`}>
              <button className="inline-flex items-center gap-1.5 px-4 py-2 overline text-muted-foreground group-hover:text-white transition-colors">
                {g.label} <CaretDown size={12} className="group-hover:rotate-180 transition-transform duration-200" />
              </button>
              <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 min-w-52">
                <div className="rounded-xl border border-white/10 bg-[#0b0b0b]/95 backdrop-blur-xl p-2 shadow-2xl">
                  {g.items.map((it) => (
                    <NavLink key={it.to} to={it.to} className={itemClass} data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}>
                      {it.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link to="/cart" className="relative text-muted-foreground hover:text-white transition-colors" data-testid="nav-cart">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] font-semibold h-4 min-w-4 px-1 rounded-full grid place-items-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden sm:flex items-center gap-3">
              {user.role === "admin" && (
                <Link to="/admin" className="overline text-secondary hover:opacity-80" data-testid="nav-admin">Admin</Link>
              )}
              <Link to="/account" className="text-muted-foreground hover:text-white transition-colors" data-testid="nav-account">
                <User size={22} />
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} className="text-muted-foreground hover:text-primary transition-colors" data-testid="nav-logout">
                <SignOut size={20} />
              </button>
            </div>
          ) : (
            <Link to="/auth" className="hidden sm:inline-block overline px-4 py-2 rounded-full border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" data-testid="nav-login">
              Sign In
            </Link>
          )}

          <button className="lg:hidden text-white" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass-header border-t border-white/5 px-5 py-4 max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
          {GROUPS.map((g) => (
            <div key={g.label} className="mb-4">
              <p className="overline text-primary mb-1.5">{g.label}</p>
              <div className="flex flex-col">
                {g.items.map((it) => (
                  <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className={itemClass}>{it.label}</NavLink>
                ))}
              </div>
            </div>
          ))}
          <div className="h-px bg-white/10 my-2" />
          {user ? (
            <>
              <Link to="/account" onClick={() => setOpen(false)} className="block py-2 overline text-white">Account</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block py-2 overline text-secondary">Admin</Link>}
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="block py-2 overline text-primary text-left">Sign Out</button>
            </>
          ) : (
            <Link to="/auth" onClick={() => setOpen(false)} className="block py-2 overline text-primary">Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
}
